import { useMemo, useState } from 'react';
import { MapPin, Trash2, Plus } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DataTable } from '@/components/glitch-ui/data-table';
import { IconButton } from '@/components/glitch-ui/icon-button';
import { ConformationModal } from '@/components/glitch-ui/confirmation-modal';
import { RadiusForm, type RadiusFormValue } from '@/components/business/business-locations/radius-form';
import type { CampaignProximityTarget, CampaignProximityInput } from '@/lib/types';

interface CampaignRadiusTargetsProps {
  businessId?: number;
  disabled?: boolean;
  /** From campaign.proximity_targets — the campaign's linked subset of the catalog. */
  currentTargets: CampaignProximityTarget[];
  /** draft.proximityTargets — the edited write shape. undefined when untouched. */
  value: CampaignProximityInput[] | null | undefined;
  onChange: (targets: CampaignProximityInput[]) => void;
}

// A display row is the write-shape radius.
type RadiusRow = CampaignProximityInput;

const radiusLabel = (row: RadiusRow): string =>
  row.address && row.address.trim().length > 0 ? row.address : `${row.radius_km} km radius`;

// Seed an editable write-shape list from the current displayed rows so switching from
// "inherited display" to "owned edit" loses nothing.
const toInputList = (currentTargets: CampaignProximityTarget[]): CampaignProximityInput[] =>
  currentTargets.map((t) => ({
    latitude: t.latitude,
    longitude: t.longitude,
    radius_km: t.radius_km,
    address: t.address ?? undefined,
  }));

export function CampaignRadiusTargets({
  businessId,
  disabled = false,
  currentTargets,
  value,
  onChange,
}: CampaignRadiusTargetsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  // Effective display list: the edited draft when set, otherwise the campaign's
  // current linked radii.
  const rows: RadiusRow[] = useMemo(() => {
    if (value !== undefined && value !== null) {
      return value;
    }
    return currentTargets.map((t) => ({
      latitude: t.latitude,
      longitude: t.longitude,
      radius_km: t.radius_km,
      address: t.address ?? undefined,
    }));
  }, [value, currentTargets]);

  // The edit base: the draft value when set, otherwise the current targets seeded
  // into write shape so the first add/remove carries existing rows forward.
  const editBase = (): CampaignProximityInput[] =>
    value !== undefined && value !== null ? value : toInputList(currentTargets);

  const appendRadius = (v: RadiusFormValue) => {
    const next = [
      ...editBase(),
      {
        latitude: v.lat,
        longitude: v.lng,
        radius_km: v.radiusKm,
        address: v.formattedAddress,
      },
    ];
    onChange(next);
    setIsModalOpen(false);
  };

  const removeRadius = (index: number) => {
    const next = editBase().filter((_, i) => i !== index);
    onChange(next);
    setDeletingIndex(null);
  };

  const columns: ColumnDef<RadiusRow>[] = [
    {
      accessorKey: 'address',
      header: 'Location',
      meta: { className: 'w-1/2 px-6' },
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <MapPin className="h-4 w-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-900">{radiusLabel(row.original)}</span>
        </div>
      ),
    },
    {
      accessorKey: 'radius_km',
      header: 'Radius',
      enableSorting: false,
      cell: ({ row }) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
          {row.original.radius_km} km
        </span>
      ),
    },
    {
      id: 'actions',
      header: () => null,
      enableSorting: false,
      meta: { className: 'w-12 px-3 py-3' },
      cell: ({ row }) =>
        !disabled ? (
          <IconButton variant="destructive" aria-label="Remove radius" onClick={() => setDeletingIndex(row.index)}>
            <Trash2 className="h-4 w-4" />
          </IconButton>
        ) : null,
    },
  ];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Radius targets</h3>
          <p className="text-sm text-gray-700 mt-1">Target a radius around specific points for your ads.</p>
        </div>
        {!disabled && (
          <Button
            variant="outline"
            className="text-gray-900"
            onClick={() => setIsModalOpen(true)}
            disabled={businessId == null}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add radius
          </Button>
        )}
      </div>

      <DataTable
        data={rows}
        columns={columns}
        variant="bordered"
        pageSize={999}
        emptyIcon={MapPin}
        emptyTitle="No Radius Targets"
        emptyDescription="Add a radius to target a circular area around specific points."
      />

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="flex max-h-[88vh] flex-col sm:max-w-[640px]" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>Add radius</DialogTitle>
          </DialogHeader>

          <div className="min-h-0 flex-1 overflow-y-auto pr-1">
            {businessId != null && (
              <RadiusForm businessId={businessId} onSubmit={appendRadius} submitLabel="Add radius" />
            )}
          </div>
        </DialogContent>
      </Dialog>

      <ConformationModal
        isOpen={deletingIndex !== null}
        onClose={() => setDeletingIndex(null)}
        onConfirm={() => deletingIndex !== null && removeRadius(deletingIndex)}
        title="Remove Radius"
        description={`Remove "${deletingIndex !== null && rows[deletingIndex] ? radiusLabel(rows[deletingIndex]) : ''}" from radius targets?`}
        confirmText="Remove"
        cancelText="Cancel"
        isLoading={false}
      />
    </div>
  );
}
